import type { ObraMapItem, ObraStatus } from '@obras/shared';
import { OBRA_STATUS_LABELS } from '@obras/shared';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { HardHat, MapPinOff, Minus, Plus, Scan, type LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { TRANSITION } from '../../components/motion.js';
import { CoverImage, CoverPlaceholder } from '../../components/ui/Cover.js';
import { Skeleton } from '../../components/ui/Feedback.js';
import { PADRAO, ZOOM_PADRAO } from '../../components/ui/MapaLocal.js';
import { cn } from '../../lib/cn.js';
import { PainelDaObra } from './PainelDaObra.js';
import { CASA_SVG, CasaDaSituacao, COR_NO_MAPA } from './SituacaoNoMapa.js';

/**
 * Os ladrilhos do mapa: OpenStreetMap, os mesmos do formulário da obra — sem
 * chave nem conta. O cinza vem do filtro de CSS em `.mapa-obras` (theme.css):
 * sem cor, claro e de pouco contraste, o mapa vira fundo e os marcadores são o
 * que se vê. Um estilo já limpo de origem, como o Positron do CARTO, pede conta
 * e chave; trocar é mudar esta constante e tirar o filtro.
 */
const LADRILHOS = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  atribuicao: '&copy; OpenStreetMap',
  zoomMaximo: 19,
} as const;

/** Perto o bastante para ver a quadra quando uma obra é escolhida na lista. */
const ZOOM_DA_OBRA = 16;

/** O voo da câmera até a obra escolhida na lista. */
const DURACAO_DO_VOO_MS = 600;

/** Folga das casinhas às bordas do mapa: longe da legenda e dos botões de zoom. */
const FOLGA_DAS_BORDAS = 64;

/** Até onde a fila de entrada dos marcadores vai: do 21º em diante, todos juntos. */
const MARCADORES_ESCALONADOS = 20;
const PASSO_DOS_MARCADORES_MS = 25;

/** A ordem das situações na legenda — a do dia a dia, não a alfabética. */
const SITUACOES: readonly ObraStatus[] = ['em_andamento', 'paralisada', 'concluida', 'cancelada'];

const NA_LEGENDA: Record<ObraStatus, readonly [string, string]> = {
  em_andamento: ['em andamento', 'em andamento'],
  paralisada: ['paralisada', 'paralisadas'],
  concluida: ['concluída', 'concluídas'],
  cancelada: ['cancelada', 'canceladas'],
};

/**
 * O painel inteiro: a lista recolhida à esquerda, o mapa e a coluna da obra à
 * direita. No celular, um embaixo do outro: o mapa, a obra aberta, a lista.
 *
 * Sem borda e sem altura própria: a página é de tela cheia, e o painel ocupa o
 * que sobra abaixo da barra de cima — colado no trilho, na borda direita e no
 * pé da janela.
 */
const PAINEL = 'flex flex-col overflow-hidden bg-surface lg:min-h-[420px] lg:flex-1 lg:flex-row';
const AREA_DO_MAPA = 'relative order-1 h-[62dvh] min-h-[360px] lg:order-2 lg:h-auto lg:flex-1';

/*
 * A lista ocupa 72px no layout — só as miniaturas. Sob o ponteiro, ou com o
 * foco dentro, ela cresce para 360px **por cima do mapa**: o lugar dela no
 * layout não muda, e o mapa não se mexe a cada passada do ponteiro. Abre com
 * um respiro de 75ms e fecha com 150ms, para não piscar a quem só atravessa.
 */
const LUGAR_DA_LISTA =
  'relative order-3 flex min-h-0 flex-col border-t border-line lg:order-1 lg:w-[72px] lg:shrink-0 lg:border-t-0';
const LISTA =
  'group/lista flex min-h-0 flex-1 flex-col bg-surface lg:absolute lg:inset-y-0 lg:left-0 lg:z-20 lg:w-[72px] lg:overflow-hidden lg:border-r lg:border-line lg:transition-[width,box-shadow] lg:delay-150 lg:duration-300 lg:ease-out-quint lg:hover:w-[360px] lg:hover:shadow-pop lg:hover:delay-75 lg:focus-within:w-[360px] lg:focus-within:shadow-pop';
/** O que só aparece com a lista aberta: o texto ao lado da miniatura. */
const SO_ABERTA =
  'lg:opacity-0 lg:transition-opacity lg:duration-200 lg:group-hover/lista:opacity-100 lg:group-focus-within/lista:opacity-100';

/** Obra que tem ponto: a que vira marcador. */
type ObraNoMapa = ObraMapItem & { readonly latitude: number; readonly longitude: number };

const temPonto = (obra: ObraMapItem): obra is ObraNoMapa =>
  obra.latitude !== null && obra.longitude !== null;

interface Selecao {
  readonly id: string;
  /** Da lista, o mapa voa até a obra; do mapa, ela já está à vista. */
  readonly origem: 'lista' | 'mapa';
}

const movimentoReduzido = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * O marcador: a casinha na cor da situação, com a base no ponto da obra. A
 * forma vem de `CASA_SVG` e do CSS (`.mapa-casa`, theme.css); daqui saem a cor
 * e a vez na fila de entrada.
 */
function icone(status: ObraStatus, atrasoMs: number): L.DivIcon {
  return L.divIcon({
    className: 'mapa-marcador',
    html: `<span class="mapa-marcador-forma" style="--cor: ${COR_NO_MAPA[status]}; animation-delay: ${atrasoMs}ms">${CASA_SVG}</span>`,
    iconSize: [22, 22],
    // O pé das paredes é o ponto da obra.
    iconAnchor: [11, 20],
    // A dica sai de acima do telhado, com folga até para a casinha escolhida, que cresce 45%.
    tooltipAnchor: [0, -26],
  });
}

/**
 * O miolo da dica da casinha: o nome da obra como texto, nunca como HTML — o
 * nome vem do cadastro, e o Leaflet poria uma string direto no `innerHTML`.
 */
function dica(nome: string): HTMLElement {
  const texto = document.createElement('span');
  texto.className = 'mapa-dica-texto';
  texto.textContent = nome;
  return texto;
}

/** Leva a câmera até que todas as obras caibam, com folga nas bordas. */
function enquadrar(mapa: L.Map, obras: readonly ObraNoMapa[], animar: boolean): void {
  const [primeira] = obras;
  if (!primeira) return;

  if (obras.length === 1) {
    mapa.setView([primeira.latitude, primeira.longitude], ZOOM_DA_OBRA, { animate: animar });
    return;
  }

  mapa.fitBounds(L.latLngBounds(obras.map((obra) => [obra.latitude, obra.longitude])), {
    padding: [FOLGA_DAS_BORDAS, FOLGA_DAS_BORDAS],
    maxZoom: ZOOM_DA_OBRA,
    animate: animar,
  });
}

function Miniatura({ obra }: { obra: ObraMapItem }) {
  return (
    <span className="relative size-11 shrink-0 overflow-hidden bg-sunken">
      {obra.coverUrl ? (
        <CoverImage src={obra.coverUrl} />
      ) : (
        <span className="grid size-full place-items-center text-ink-3">
          <HardHat size={16} strokeWidth={1.5} aria-hidden />
        </span>
      )}
      {/* A situação no canto da miniatura: com a lista recolhida, é o que resta dela. */}
      <CasaDaSituacao status={obra.status} className="absolute right-0.5 bottom-0.5 z-10" />
    </span>
  );
}

/** O texto da linha, ao lado da miniatura — com a lista recolhida, escondido. */
function TextoDaLinha({ obra, detalhe }: { obra: ObraMapItem; detalhe: ReactNode }) {
  return (
    <span className={cn('min-w-0 flex-1', SO_ABERTA)}>
      <span className="block truncate text-[13px] font-semibold tracking-tight text-ink">
        {obra.name}
      </span>
      <span className="mt-1 flex items-center gap-1.5 text-[12px] whitespace-nowrap text-ink-3">
        <span className="shrink-0 font-mono">{obra.contractNumber ?? 'sem contrato'}</span>
        <span aria-hidden>·</span>
        {detalhe}
      </span>
    </span>
  );
}

const LINHA =
  'relative flex w-full items-center gap-4 border-b border-line px-[14px] py-3 text-left transition-colors duration-100';

/**
 * Uma obra com ponto, na lista.
 *
 * Escolher a linha leva o mapa até a obra e abre a coluna dela; passar o
 * ponteiro realça a casinha, e o contrário também — a linha e a casinha são a
 * mesma obra em dois lugares. A escolhida leva a barra laranja à esquerda, a
 * mesma do item ativo do trilho, visível também com a lista recolhida.
 */
const LinhaComPonto = memo(function LinhaComPonto({
  obra,
  ativa,
  realcada,
  onEscolher,
  onRealce,
  registrar,
}: {
  obra: ObraNoMapa;
  ativa: boolean;
  realcada: boolean;
  onEscolher: (id: string) => void;
  onRealce: (id: string | null) => void;
  registrar: (id: string, elemento: HTMLElement | null) => void;
}) {
  return (
    <li>
      <button
        type="button"
        ref={(elemento) => registrar(obra.id, elemento)}
        aria-pressed={ativa}
        onClick={() => onEscolher(obra.id)}
        onMouseEnter={() => onRealce(obra.id)}
        onMouseLeave={() => onRealce(null)}
        onFocus={() => onRealce(obra.id)}
        onBlur={() => onRealce(null)}
        className={cn(LINHA, ativa || realcada ? 'bg-sunken' : 'hover:bg-sunken')}
      >
        {ativa ? <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-primary" /> : null}
        <Miniatura obra={obra} />
        <TextoDaLinha
          obra={obra}
          detalhe={<span className="truncate">{OBRA_STATUS_LABELS[obra.status]}</span>}
        />
      </button>
    </li>
  );
});

/** Obra sem coordenada: não tem casinha no mapa, e a linha só abre a coluna da obra. */
const LinhaSemPonto = memo(function LinhaSemPonto({
  obra,
  ativa,
  onEscolher,
  registrar,
}: {
  obra: ObraMapItem;
  ativa: boolean;
  onEscolher: (id: string) => void;
  registrar: (id: string, elemento: HTMLElement | null) => void;
}) {
  return (
    <li>
      <button
        type="button"
        ref={(elemento) => registrar(obra.id, elemento)}
        aria-pressed={ativa}
        onClick={() => onEscolher(obra.id)}
        className={cn(LINHA, ativa ? 'bg-sunken' : 'hover:bg-sunken')}
      >
        {ativa ? <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-primary" /> : null}
        <Miniatura obra={obra} />
        <TextoDaLinha
          obra={obra}
          detalhe={
            <>
              <MapPinOff size={13} strokeWidth={1.5} className="shrink-0" aria-hidden />
              <span className="truncate">sem localização</span>
            </>
          }
        />
      </button>
    </li>
  );
});

function ListaDoMapa({
  comPonto,
  semPonto,
  selecionada,
  realce,
  onEscolher,
  onRealce,
}: {
  comPonto: readonly ObraNoMapa[];
  semPonto: readonly ObraMapItem[];
  selecionada: string | null;
  realce: string | null;
  onEscolher: (id: string) => void;
  onRealce: (id: string | null) => void;
}) {
  const listaRef = useRef<HTMLUListElement>(null);
  const linhas = useRef(new Map<string, HTMLElement>());

  const registrar = useCallback((id: string, elemento: HTMLElement | null) => {
    if (elemento) linhas.current.set(id, elemento);
    else linhas.current.delete(id);
  }, []);

  /*
   * A obra escolhida no mapa entra na vista da lista — rolando só a lista. O
   * `scrollIntoView` rolaria também a página: no celular, onde a lista fica no
   * fim, escolher uma casinha levaria a tela para longe da coluna que abriu.
   */
  useEffect(() => {
    const lista = listaRef.current;
    const linha = selecionada ? linhas.current.get(selecionada) : undefined;
    if (!lista || !linha) return;

    const caixa = lista.getBoundingClientRect();
    const alvo = linha.getBoundingClientRect();
    if (alvo.top < caixa.top) lista.scrollTop -= caixa.top - alvo.top;
    else if (alvo.bottom > caixa.bottom) lista.scrollTop += alvo.bottom - caixa.bottom;
  }, [selecionada]);

  return (
    <nav aria-label="Obras do recorte" className={LUGAR_DA_LISTA}>
      <div className={LISTA}>
        <div className="flex h-11 shrink-0 items-center gap-4 border-b border-line px-[14px] text-[12px] text-ink-3">
          <span className="tabular w-11 shrink-0 text-center font-medium text-ink">
            {comPonto.length + semPonto.length}
          </span>
          <span className={cn('truncate whitespace-nowrap', SO_ABERTA)}>
            {comPonto.length + semPonto.length === 1 ? 'obra' : 'obras'}
            {semPonto.length > 0 ? ` · ${semPonto.length} sem localização` : ''}
          </span>
        </div>

        <ul
          ref={listaRef}
          className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {comPonto.map((obra) => (
            <LinhaComPonto
              key={obra.id}
              obra={obra}
              ativa={obra.id === selecionada}
              realcada={obra.id === realce}
              onEscolher={onEscolher}
              onRealce={onRealce}
              registrar={registrar}
            />
          ))}

          {semPonto.length > 0 ? (
            <>
              <li className="sticky top-0 z-10 flex items-center gap-4 border-b border-line bg-surface px-[14px] py-2 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
                <span className="grid w-11 shrink-0 place-items-center">
                  <MapPinOff size={13} strokeWidth={1.5} aria-hidden />
                </span>
                <span className={cn('whitespace-nowrap', SO_ABERTA)}>Sem localização</span>
              </li>
              {semPonto.map((obra) => (
                <LinhaSemPonto
                  key={obra.id}
                  obra={obra}
                  ativa={obra.id === selecionada}
                  onEscolher={onEscolher}
                  registrar={registrar}
                />
              ))}
            </>
          ) : null}
        </ul>
      </div>
    </nav>
  );
}

function BotaoDoMapa({
  titulo,
  onClick,
  children,
}: {
  titulo: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      className="grid size-9 place-items-center text-ink-2 transition-colors duration-100 hover:bg-sunken hover:text-ink"
    >
      {children}
    </button>
  );
}

/**
 * O mapa, com uma casinha por obra.
 *
 * O Leaflet é montado uma vez e conduzido por efeitos: as casinhas são
 * refeitas só quando o conjunto muda de verdade — outra obra, outra situação,
 * outro ponto —, não a cada revalidação, senão entrariam de novo a cada volta
 * à aba. Clicar numa casinha abre a coluna da obra; sob o ponteiro, ela cresce
 * e o nome da obra aparece na dica, acima do telhado.
 */
function MapaDasObras({
  obras,
  selecao,
  realce,
  onEscolher,
  onRealce,
  onFecharTudo,
}: {
  obras: readonly ObraNoMapa[];
  selecao: Selecao | null;
  realce: string | null;
  onEscolher: (id: string) => void;
  onRealce: (id: string | null) => void;
  /** Clique no fundo do mapa: a pessoa terminou de olhar. */
  onFecharTudo: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const camadaRef = useRef<L.LayerGroup | null>(null);
  const marcadoresRef = useRef(new Map<string, L.Marker>());
  /** O conjunto enquadrado por último: só um conjunto novo move a câmera sozinho. */
  const enquadradoRef = useRef<string | null>(null);
  /** A escolha já atendida: revalidar a lista não faz o mapa voar de novo. */
  const atendidaRef = useRef<Selecao | null>(null);
  /** Até quando a câmera voa para uma obra: durante o voo, nada a empurra. */
  const vooAteRef = useRef(0);

  /*
   * A lista, a escolha e as funções mais recentes, para os ouvintes do
   * Leaflet. Trocadas num efeito declarado antes dos que as usam — os efeitos
   * rodam na ordem.
   */
  const obrasRef = useRef(obras);
  const selecaoRef = useRef(selecao);
  const onEscolherRef = useRef(onEscolher);
  const onRealceRef = useRef(onRealce);
  const onFecharTudoRef = useRef(onFecharTudo);
  useEffect(() => {
    obrasRef.current = obras;
    selecaoRef.current = selecao;
    onEscolherRef.current = onEscolher;
    onRealceRef.current = onRealce;
    onFecharTudoRef.current = onFecharTudo;
  }, [obras, selecao, onEscolher, onRealce, onFecharTudo]);

  /** Muda quando muda o que a casinha mostra; revalidar com o mesmo conteúdo, não. */
  const chave = useMemo(
    () =>
      obras
        .map((obra) => `${obra.id}:${obra.status}:${obra.latitude}:${obra.longitude}:${obra.name}`)
        .join('|'),
    [obras],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const mapa = L.map(container, {
      center: PADRAO,
      zoom: ZOOM_PADRAO,
      zoomControl: false,
      scrollWheelZoom: true,
    });
    mapa.attributionControl.setPrefix(false);

    L.tileLayer(LADRILHOS.url, {
      maxZoom: LADRILHOS.zoomMaximo,
      attribution: LADRILHOS.atribuicao,
    }).addTo(mapa);

    const camada = L.layerGroup().addTo(mapa);

    // A casinha não repassa o clique ao mapa: este é só o do fundo.
    mapa.on('click', () => onFecharTudoRef.current());

    /*
     * A coluna da obra abre, o trilho recolhe, a janela muda: o Leaflet só
     * sabe o novo tamanho medindo, e o canto superior esquerdo fica onde está.
     * A obra escolhida que estava à vista continua à vista — a borda que avança
     * a empurra para dentro, quadro a quadro, em vez de cobri-la. Durante o voo
     * até uma obra, não: o voo já termina com ela no meio.
     */
    const observador = new ResizeObserver(() => {
      const escolhida = selecaoRef.current;
      const marcador = escolhida ? marcadoresRef.current.get(escolhida.id) : undefined;
      const estavaAVista = marcador ? mapa.getBounds().contains(marcador.getLatLng()) : false;

      mapa.invalidateSize({ pan: false });

      if (marcador && estavaAVista && performance.now() >= vooAteRef.current) {
        mapa.panInside(marcador.getLatLng(), {
          padding: [FOLGA_DAS_BORDAS, FOLGA_DAS_BORDAS],
          animate: false,
        });
      }
    });
    observador.observe(container);

    mapaRef.current = mapa;
    camadaRef.current = camada;
    const marcadores = marcadoresRef.current;

    return () => {
      observador.disconnect();
      mapa.remove();
      mapaRef.current = null;
      camadaRef.current = null;
      marcadores.clear();
      enquadradoRef.current = null;
      atendidaRef.current = null;
    };
  }, []);

  // As casinhas, refeitas quando o conjunto muda.
  useEffect(() => {
    const mapa = mapaRef.current;
    const camada = camadaRef.current;
    if (!mapa || !camada) return;

    const marcadores = marcadoresRef.current;
    camada.clearLayers();
    marcadores.clear();

    const reduzido = movimentoReduzido();

    obrasRef.current.forEach((obra, posicao) => {
      const atraso = reduzido
        ? 0
        : Math.min(posicao, MARCADORES_ESCALONADOS) * PASSO_DOS_MARCADORES_MS;

      /*
       * O nome vai numa dica do sistema, e não no `title`, que abria a dica crua
       * do navegador. A dica do Leaflet abre sob o ponteiro, no toque e com o foco
       * do teclado, e liga a casinha a ela (`aria-describedby`); o `aria-label`
       * dá à casinha o nome e a situação, que no mapa só a cor dizia.
       */
      const marcador = L.marker([obra.latitude, obra.longitude], {
        icon: icone(obra.status, atraso),
        keyboard: true,
      })
        .bindTooltip(dica(obra.name), {
          className: 'mapa-dica',
          direction: 'top',
          offset: [0, -4],
          opacity: 1,
        })
        .on('click', () => onEscolherRef.current(obra.id))
        .on('mouseover', () => onRealceRef.current(obra.id))
        .on('mouseout', () => onRealceRef.current(null))
        .addTo(camada);

      /*
       * O foco do teclado realça a casinha como o ponteiro, e Enter ou espaço a
       * escolhem, como em qualquer botão. O Leaflet dá à casinha o papel de
       * botão e o foco, mas o Enter dele só abre o balão próprio, que aqui não há.
       */
      const elemento = marcador.getElement();
      elemento?.setAttribute('aria-label', `${obra.name}, ${OBRA_STATUS_LABELS[obra.status]}`);
      elemento?.addEventListener('focus', () => onRealceRef.current(obra.id));
      elemento?.addEventListener('blur', () => onRealceRef.current(null));
      elemento?.addEventListener('keydown', (evento) => {
        if (evento.key !== 'Enter' && evento.key !== ' ') return;
        evento.preventDefault();
        onEscolherRef.current(obra.id);
      });

      marcadores.set(obra.id, marcador);
    });

    const conjunto = obrasRef.current.map((obra) => obra.id).join(',');
    if (conjunto !== enquadradoRef.current) {
      const primeiraVez = enquadradoRef.current === null;
      enquadradoRef.current = conjunto;
      enquadrar(mapa, obrasRef.current, !primeiraVez && !reduzido);
    }
  }, [chave]);

  // O realce: a linha sob o ponteiro acende a casinha.
  useEffect(() => {
    for (const [id, marcador] of marcadoresRef.current) {
      marcador.getElement()?.toggleAttribute('data-realce', id === realce);
    }
  }, [realce, chave]);

  // A escolha: a casinha cresce, e da lista a câmera voa até a obra.
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;

    for (const [id, marcador] of marcadoresRef.current) {
      const ativo = id === selecao?.id;
      marcador.getElement()?.toggleAttribute('data-ativo', ativo);
      marcador.setZIndexOffset(ativo ? 1000 : 0);
    }

    const nova = atendidaRef.current !== selecao;
    atendidaRef.current = selecao;
    if (!selecao || !nova || selecao.origem !== 'lista') return;

    const marcador = marcadoresRef.current.get(selecao.id);
    if (!marcador) return;

    const zoom = Math.max(mapa.getZoom(), ZOOM_DA_OBRA);
    if (movimentoReduzido()) {
      mapa.setView(marcador.getLatLng(), zoom, { animate: false });
      return;
    }
    vooAteRef.current = performance.now() + DURACAO_DO_VOO_MS;
    mapa.flyTo(marcador.getLatLng(), zoom, { duration: DURACAO_DO_VOO_MS / 1000 });
  }, [selecao, chave]);

  return (
    <>
      <div
        ref={containerRef}
        role="region"
        aria-label="Mapa das obras"
        className="mapa-obras absolute inset-0 isolate"
      />

      <div className="absolute top-3 right-3 z-10 flex flex-col divide-y divide-line border border-line bg-surface">
        <BotaoDoMapa titulo="Aproximar" onClick={() => mapaRef.current?.zoomIn()}>
          <Plus size={16} strokeWidth={1.5} aria-hidden />
        </BotaoDoMapa>
        <BotaoDoMapa titulo="Afastar" onClick={() => mapaRef.current?.zoomOut()}>
          <Minus size={16} strokeWidth={1.5} aria-hidden />
        </BotaoDoMapa>
        <BotaoDoMapa
          titulo="Enquadrar todas as obras"
          onClick={() => {
            const mapa = mapaRef.current;
            if (mapa) enquadrar(mapa, obras, !movimentoReduzido());
          }}
        >
          <Scan size={16} strokeWidth={1.5} aria-hidden />
        </BotaoDoMapa>
      </div>
    </>
  );
}

/** A legenda: a cor de cada situação e quantas obras dela estão no mapa. */
function Legenda({ obras, semPonto }: { obras: readonly ObraNoMapa[]; semPonto: number }) {
  const contagem = useMemo(() => {
    const porSituacao: Record<ObraStatus, number> = {
      em_andamento: 0,
      paralisada: 0,
      concluida: 0,
      cancelada: 0,
    };
    for (const obra of obras) porSituacao[obra.status] += 1;
    return porSituacao;
  }, [obras]);

  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-wrap items-center gap-x-4 gap-y-1 border border-line bg-surface px-3 py-2 text-[12px] text-ink-3">
      {SITUACOES.filter((situacao) => contagem[situacao] > 0).map((situacao) => (
        <span key={situacao} className="flex items-center gap-1.5">
          <CasaDaSituacao status={situacao} />
          <span className="tabular font-medium text-ink">{contagem[situacao]}</span>
          {NA_LEGENDA[situacao][contagem[situacao] === 1 ? 0 : 1]}
        </span>
      ))}
      {semPonto > 0 ? (
        <span className="flex items-center gap-1.5">
          <MapPinOff size={13} strokeWidth={1.5} className="shrink-0" aria-hidden />
          <span className="tabular font-medium text-ink">{semPonto}</span>
          sem localização
        </span>
      ) : null}
    </div>
  );
}

/**
 * Um aviso sobre o mapa, no lugar dele: o mapa continua ali, velado, e a
 * página não troca de forma quando o recorte esvazia.
 */
export function AvisoNoMapa({
  icone: Icone,
  titulo,
  children,
  acao,
}: {
  icone: LucideIcon;
  titulo: string;
  children: ReactNode;
  acao?: ReactNode | undefined;
}) {
  return (
    <div className="absolute inset-0 z-10 grid place-items-center bg-surface/70 p-6 text-center">
      <div className="max-w-sm">
        <span className="mx-auto grid size-11 place-items-center border border-line bg-sunken">
          <Icone size={20} strokeWidth={1.5} className="text-ink-3" aria-hidden />
        </span>
        <p className="mt-3 text-[15px] font-semibold tracking-tight">{titulo}</p>
        <p className="mt-1 text-[13px] text-ink-3">{children}</p>
        {acao ? <div className="mt-4 flex justify-center">{acao}</div> : null}
      </div>
    </div>
  );
}

/** Nenhuma obra do recorte tem coordenada: o mapa diz isso, em vez de ficar vazio calado. */
function SemPontos() {
  return (
    <AvisoNoMapa icone={MapPinOff} titulo="Nenhuma obra com localização">
      As obras deste recorte ainda não têm coordenada. A localização é definida no cadastro da obra.
    </AvisoNoMapa>
  );
}

/** É campo de texto ou o visualizador de fotos: o Esc é deles, não do mapa. */
const escDeOutro = (alvo: EventTarget | null): boolean =>
  alvo instanceof Element &&
  alvo.closest('dialog, input, textarea, select, [contenteditable="true"]') !== null;

/**
 * O Mapa de Obras (design.md §8, Mapa de Obras).
 *
 * A lista recolhida à esquerda, o mapa limpo no meio, com uma casinha por obra
 * na cor da situação, e a coluna da obra à direita, que é parte do conteúdo.
 * Clicar numa casinha — ou numa linha da lista — abre a coluna com o resumo da
 * obra; não há cartão sobre o mapa repetindo o que a coluna já mostra. A lista
 * e o mapa são a mesma escolha: escolher numa ponta acende a outra, e com a
 * coluna aberta, escolher outra obra troca o que ela mostra.
 */
export function ObrasMapa({
  obras,
  aviso,
}: {
  obras: readonly ObraMapItem[];
  /** O que dizer sobre o mapa quando o recorte veio vazio; toma o lugar do "sem localização". */
  aviso?: ReactNode | undefined;
}) {
  const [selecao, setSelecao] = useState<Selecao | null>(null);
  const [realce, setRealce] = useState<string | null>(null);
  const [detalhes, setDetalhes] = useState(false);

  // Obra que saiu do recorte deixa de estar escolhida — ajustado na renderização.
  if (selecao && !obras.some((obra) => obra.id === selecao.id)) setSelecao(null);
  // Sem obra escolhida não há coluna aberta.
  if (detalhes && !selecao) setDetalhes(false);

  const comPonto = useMemo(() => obras.filter(temPonto), [obras]);
  const semPonto = useMemo(() => obras.filter((obra) => !temPonto(obra)), [obras]);
  const selecionada = selecao ? obras.find((obra) => obra.id === selecao.id) : undefined;

  // Escolher abre a coluna. Da lista, o mapa voa até a obra.
  const escolherNaLista = useCallback((id: string) => {
    setSelecao({ id, origem: 'lista' });
    setDetalhes(true);
  }, []);
  // A casinha já escolhida, clicada de novo, só garante a coluna aberta.
  const escolherNoMapa = useCallback((id: string) => {
    setSelecao((atual) => (atual?.id === id ? atual : { id, origem: 'mapa' }));
    setDetalhes(true);
  }, []);
  /** Recolher mantém a obra escolhida: a faixa "Detalhes da obra" a reabre. */
  const recolherDetalhes = useCallback(() => setDetalhes(false), []);
  /** Clique no fundo do mapa: a pessoa terminou de olhar, e nada fica aberto. */
  const fecharTudo = useCallback(() => {
    setDetalhes(false);
    setSelecao(null);
  }, []);

  // Esc recolhe a coluna; com ela recolhida, desfaz a escolha.
  useEffect(() => {
    if (!detalhes && !selecao) return undefined;

    const aoTeclar = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented || escDeOutro(event.target)) return;
      if (detalhes) recolherDetalhes();
      else fecharTudo();
    };

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [detalhes, selecao, recolherDetalhes, fecharTudo]);

  return (
    // Colado nas bordas, o painel só aparece: subir abriria uma fresta no pé da janela.
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={TRANSITION.base}
      className={PAINEL}
    >
      <ListaDoMapa
        comPonto={comPonto}
        semPonto={semPonto}
        selecionada={selecao?.id ?? null}
        realce={realce}
        onEscolher={escolherNaLista}
        onRealce={setRealce}
      />

      <div className={AREA_DO_MAPA}>
        <MapaDasObras
          obras={comPonto}
          selecao={selecao}
          realce={realce}
          onEscolher={escolherNoMapa}
          onRealce={setRealce}
          onFecharTudo={fecharTudo}
        />
        {obras.length > 0 ? <Legenda obras={comPonto} semPonto={semPonto.length} /> : null}
        {aviso ?? (comPonto.length === 0 ? <SemPontos /> : null)}
      </div>

      <PainelDaObra
        obra={selecionada ?? null}
        aberto={detalhes}
        onAbrir={() => setDetalhes(true)}
        onRecolher={recolherDetalhes}
      />
    </motion.div>
  );
}

/** O painel enquanto o recorte não chega: as miniaturas, o visor e a faixa da obra. */
export function ObrasMapaSkeleton() {
  return (
    <div aria-hidden className={PAINEL}>
      <div className={LUGAR_DA_LISTA}>
        <div className="flex min-h-0 flex-1 flex-col lg:border-r lg:border-line">
          <div className="h-11 shrink-0 border-b border-line" />
          {Array.from({ length: 7 }, (_, linha) => (
            <div
              key={linha}
              className="flex items-center gap-4 border-b border-line px-[14px] py-3"
            >
              <Skeleton className="size-11 shrink-0 rounded-none" />
              <div className="min-w-0 flex-1 lg:hidden">
                <Skeleton className="h-3.5 w-3/4 rounded-none" />
                <Skeleton className="mt-2 h-3 w-1/2 rounded-none" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={AREA_DO_MAPA}>
        <CoverPlaceholder busy />
      </div>
      <div className="hidden w-11 shrink-0 border-l border-line lg:order-3 lg:block" />
    </div>
  );
}
